// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { sizeVariantValues } from "@ces/MobiusOverrides/FormField/formStyles";
import Calendar from "@ces/XDL/Icons/Calendar";
import Close from "@ces/XDL/Icons/Close";
import LeftNav from "@ces/XDL/Icons/LeftNav";
import RightNav from "@ces/XDL/Icons/RightNav";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import { xBaseTheme } from "@ces/XDL/types";
import { color } from "@ces/XDL/Utilities";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";

export default {
    defaultProps: {
        mergeCss: true,
        ...DatePickerPresentationPropsDefault,
        dateTimePickerProps: {
            showNeighboringMonth: true,
            nextLabel: React.createElement(RightNav),
            prevLabel: React.createElement(LeftNav),
        } as any,
        clearIconProps: {
            color: "common.ui12",
            src: Close,
            size: 12,
            css: css`
                &&& {
                    padding: 0;
                }
                /* XNG-Change */
                &:hover {
                    color: ${color("common.ui8")};
                }
            `,
        },
        calendarIconProps: {
            color: "text.link",
            src: Calendar,
            size: 14,
            css: css`
                &&& {
                    padding: 0;
                }
            `,
        },
        placeholders: {
            dayPlaceholder: "DD",
            monthPlaceholder: "MM",
            yearPlaceholder: "YYYY",
            hourPlaceholder: "hr",
            minutePlaceholder: "min",
        },
        cssOverrides: {
            datePicker: css`
                button:focus {
                    /* outline is added on span for border reasons */
                    outline: none;
                    span {
                        outline: none;
                    }
                }
                &&& > .react-datetime-picker {
                    height: ${({ _sizeVariant }: any) => (sizeVariantValues as any)[_sizeVariant].height}px;
                    padding: 0 9px;
                    &--disabled {
                        cursor: not-allowed;
                        background: ${color("common.disabled", 0.33)};
                        color: ${color("common.black", 0.33)};
                        & input {
                            background: none !important;
                        }
                        .react-datetime-picker__button {
                            pointer-events: none;
                        }
                        border: 1px solid ${color("common.border")};
                    }
                    .react-datetime-picker__inputGroup {
                        display: flex;
                        align-items: baseline;
                        justify-content: flex-start;
                        padding: 0;

                        &__input,
                        &__leadingZero {
                            font-size: 14px;
                            line-height: 16px;
                            font-weight: ${fontConfig.weight.regular};
                            margin: 0;
                            &,
                            &:hover,
                            &:focus {
                                border: none;
                                box-shadow: none;
                                padding: 0;
                            }
                            &::selection {
                                background-color: transparent;
                            }
                        }
                        &__year {
                            min-width: 100%;
                        }
                        &__day {
                            min-width: auto;
                        }
                        &__month {
                            min-width: auto;
                        }
                    }
                    &:not(:disabled):not(&--disabled) {
                        &:hover {
                            border: 1px solid ${color("common.ui8")};
                        }
                        &:focus-within {
                            border: 1px solid ${color("primary")};
                        }
                    }
                    .react-datetime-picker__calendar-button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-right: 8px;
                        padding: 0;
                        outline: none;
                        order: -1;
                        margin-bottom: 1px;
                        svg {
                            color: ${getColor("common.ui7")};
                        }
                    }
                    .react-datetime-picker__clear-button {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-left: 8px;
                        padding: 0;
                        outline: none;
                        &:disabled {
                            svg {
                                color: ${getColor("common.ui3")};
                            }
                        }
                        z-index: 999;
                    }
                    &:not(:disabled),
                    &:focus-within {
                        .react-datetime-picker__button {
                            padding-left: 0;
                            padding-right: 0;
                            outline: none;
                        }
                    }
                }
                .react-calendar {
                    min-width: 280px;
                    box-shadow: none;
                    margin-top: 0;
                    outline: ${getColor("common.border")} solid 2px;
                    outline-offset: -1px;
                    padding: 15px 20px 20px 20px;
                    button {
                        font-family: inherit;
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                color: ${getColor("common.white")};
                                background-color: ${getColor("common.ui4")};
                            }
                        }
                    }

                    .react-calendar__navigation {
                        justify-content: space-between;
                        margin-bottom: 15px;

                        &__arrow {
                            height: auto;
                        }

                        &__prev-button,
                        &__next-button {
                            min-width: 0;
                            padding: 0;
                            width: auto;
                            color: ${getColor("common.ui3")};
                            background-image: none;
                            background: transparent;
                            cursor: default;
                            &:enabled {
                                color: ${getColor("primary")};
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("primary")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }

                            svg {
                                width: 8px;
                                height: 12px;
                            }
                        }

                        &__label {
                            font-size: 16px;
                            font-weight: 600;
                            line-height: 18px;
                            flex-grow: 0 !important;
                            width: auto;

                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    background: transparent;
                                }
                                &:focus:not(:disabled) {
                                    color: ${getColor("text")};
                                    outline: none;
                                }

                                &:hover:not(:disabled) {
                                    color: ${getColor("common.ui8")};
                                }
                            }
                        }
                    }

                    &__month-view {
                        &__weekdays {
                            font-size: 10px;
                            font-weight: normal;
                            color: ${getColor("text")};
                            letter-spacing: 0.4px;
                            text-transform: uppercase;
                            margin-bottom: 5px;
                        }

                        &__days {
                            &__day {
                                color: ${getColor("secondary")};
                                border-radius: 0;
                                font-weight: normal;
                                font-size: 12px;
                                height: 29px;
                                width: 29px;
                                padding: 0;

                                &.react-calendar__tile--now {
                                    position: relative;
                                    &:after {
                                        content: "";
                                        position: absolute;
                                        bottom: 3px;
                                        left: 50%;
                                        transform: translateX(-50%);
                                        color: ${getColor("primary")};
                                        border: 2px solid ${getColor("primary")};
                                        border-radius: 50%;
                                        font-size: 0;
                                    }
                                }

                                &:disabled,
                                &--neighboringMonth {
                                    color: ${getColor("common.border")};
                                }
                            }
                        }
                    }

                    &__year-view,
                    &__decade-view,
                    &__century-view {
                        .react-calendar__tile {
                            color: ${getColor("secondary")};
                            padding: 5px 0;
                            font-weight: normal;
                            font-size: 12px;
                        }
                    }

                    &__tile {
                        &--hasActive:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth),
                        &--active:not(:disabled):not(.react-calendar__month-view__days__day--neighboringMonth) {
                            &:after {
                                content: none;
                            }
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
